import { useEffect, useCallback } from 'react';
import { FaviconConfig, FaviconHookReturn } from '../types/favicon';

export const useDynamicFavicon = (): FaviconHookReturn => {
  
  const setFavicon = useCallback(async (params: FaviconConfig = {}) => {
    try {
      // 기존 파비콘 제거
      const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
      existingFavicons.forEach(favicon => favicon.remove());
      
      // 백엔드 API URL 구성
      const queryString = new URLSearchParams(
        Object.entries(params).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = String(value);
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString();
      
      const apiUrl = process.env.REACT_APP_CAMPUS_API_BASE_URL || '';
      const faviconUrl = `${apiUrl}/api/favicon${queryString ? '?' + queryString : ''}`;
      
      // 새 파비콘 생성
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/x-icon';
      favicon.href = faviconUrl;
      
      // 파비콘이 로드되었는지 확인
      favicon.onload = () => {
        // /// console.log('Dynamic favicon loaded successfully');
      };
      
      favicon.onerror = () => {
        /// console.warn('Failed to load dynamic favicon, using default');
        // 기본 파비콘으로 폴백
        favicon.href = '/favicon.ico';
      };
      
      document.head.appendChild(favicon);
      
    } catch (error) {
      /// console.error('Error setting dynamic favicon:', error);
    }
  }, []);

  // 알림 개수가 포함된 파비콘 생성 (Canvas 사용)
  const setNotificationFavicon = useCallback(async (count: number, baseParams: FaviconConfig = {}) => {
    if (count <= 0) {
      setFavicon(baseParams);
      return;
    }

    try {
      // 기본 파비콘 이미지 로드
      const apiUrl = process.env.REACT_APP_CAMPUS_API_BASE_URL || '';
      const baseQueryString = new URLSearchParams(
        Object.entries(baseParams).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = String(value);
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString();
      
      const baseFaviconUrl = `${apiUrl}/api/favicon${baseQueryString ? '?' + baseQueryString : ''}`;
      
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 32;
        canvas.height = 32;
        const ctx = canvas.getContext('2d');
        
        if (!ctx) return;
        
        // 기본 파비콘 그리기
        ctx.drawImage(img, 0, 0, 32, 32);
        
        // 알림 배지 그리기
        ctx.fillStyle = '#ff4444';
        ctx.beginPath();
        ctx.arc(24, 8, 8, 0, 2 * Math.PI);
        ctx.fill();
        
        // 숫자 표시
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 10px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(count > 99 ? '99+' : count.toString(), 24, 12);
        
        // 기존 파비콘 제거 및 새 파비콘 설정
        const existingFavicons = document.querySelectorAll('link[rel="icon"], link[rel="shortcut icon"]');
        existingFavicons.forEach(favicon => favicon.remove());
        
        const favicon = document.createElement('link');
        favicon.rel = 'icon';
        favicon.type = 'image/png';
        favicon.href = canvas.toDataURL();
        
        document.head.appendChild(favicon);
      };
      
      img.onerror = () => {
        /// console.warn('Failed to load base favicon for notification badge');
        setFavicon(baseParams);
      };
      
      img.src = baseFaviconUrl;
      
    } catch (error) {
      /// console.error('Error creating notification favicon:', error);
      setFavicon(baseParams);
    }
  }, [setFavicon]);

  return {
    setFavicon,
    setNotificationFavicon
  };
};

export default useDynamicFavicon;
